import React from 'react';
import type { StaffReservation, DayPart, WeekDeclaration, ReservationAssignment } from '../lib/api';

/**
 * Week grid for the Availability tab — seven day columns across, section rows
 * down (Day / Night / Graveyard, then On Call), one card per SPOT.
 *
 * A slot with capacity 3 renders three cards sharing the slot's times:
 * spots held by a declaration show the provider's working name, the rest
 * show OPEN. Capacity still lives on the ShiftSlot — the cards are purely a
 * rendering of it. A cancelled slot collapses back to a single greyed card.
 *
 * Alongside the roster, two kinds of card come from declarations rather than
 * slots: CUSTOM hours (yellow, in the section the manager picked at approval)
 * and ON_CALL offers (the bottom row, no times, oldest declaration first so
 * the manager calls in the order they volunteered).
 *
 * Deliberately NOT modelled on the whole of Deputy: no draft/publish state,
 * no per-day cost totals, no time-off row, no meal breaks, and no per-column
 * "+" button. Adding shifts stays on the panel header button.
 */

const SECTION_ROWS: Array<{ key: DayPart; label: string }> = [
  { key: 'DAY',       label: 'Day Shifts' },
  { key: 'NIGHT',     label: 'Night Shifts' },
  { key: 'GRAVEYARD', label: 'Graveyard Shifts' },
];

interface Props {
  weekStart: Date;              // Monday
  shifts: StaffReservation[];
  declarations: WeekDeclaration[];
  onEditReservation: (shift: StaffReservation) => void;
  /** Clicking an open spot rosters someone into it. */
  onAssign: (shift: StaffReservation) => void;
  /** Clicking a rostered provider opens call-off and slot actions. */
  onSpotActions: (shift: StaffReservation, assignment: ReservationAssignment) => void;
  /** Custom-hours and called-off cards: details, call off, reinstate. */
  onDeclarationActions: (declaration: WeekDeclaration) => void;
}

export function AvailabilityWeekGrid({
  weekStart, shifts, declarations, onEditReservation, onAssign, onSpotActions, onDeclarationActions,
}: Props) {
  const days = buildDays(weekStart);
  const todayIso = isoDate(new Date());

  // key: "YYYY-MM-DD|SECTION" -> slots for that cell, earliest start first
  const byCell = new Map<string, StaffReservation[]>();
  for (const s of shifts) {
    const key = `${s.date}|${s.section}`;
    const bucket = byCell.get(key);
    if (bucket) bucket.push(s);
    else byCell.set(key, [s]);
  }
  for (const bucket of byCell.values()) {
    bucket.sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  // CUSTOM declarations sit in the section the manager chose when approving.
  // Legacy rows approved before that choice existed have no section, so they
  // fall back to the times rather than disappearing.
  const customByCell = new Map<string, WeekDeclaration[]>();
  const onCallByDay = new Map<string, WeekDeclaration[]>();
  const calledOffByDay = new Map<string, WeekDeclaration[]>();
  for (const d of declarations) {
    if (d.status === 'CALLED_OFF') {
      const bucket = calledOffByDay.get(d.date);
      if (bucket) bucket.push(d);
      else calledOffByDay.set(d.date, [d]);
      continue;
    }
    if (d.mode === 'ON_CALL') {
      const bucket = onCallByDay.get(d.date);
      if (bucket) bucket.push(d);
      else onCallByDay.set(d.date, [d]);
      continue;
    }
    const section = d.section ?? deriveSection(d.customStart, d.customEnd);
    const key = `${d.date}|${section}`;
    const bucket = customByCell.get(key);
    if (bucket) bucket.push(d);
    else customByCell.set(key, [d]);
  }

  return (
    <div className="avail-grid" role="grid">
      <div className="avail-grid-corner" />
      {days.map(d => (
        <div
          key={d.iso}
          className={`avail-grid-dayhead ${d.iso === todayIso ? 'is-today' : ''}`}
          role="columnheader"
        >
          <span className="avail-grid-dayname">{d.weekday}</span>
          <span className="avail-grid-daynum">{d.dayNum}</span>
        </div>
      ))}

      {SECTION_ROWS.map(row => (
        <React.Fragment key={row.key}>
          <div className="avail-grid-rowhead" role="rowheader">{row.label}</div>
          {days.map(d => {
            const slots = byCell.get(`${d.iso}|${row.key}`) ?? [];
            const customs = customByCell.get(`${d.iso}|${row.key}`) ?? [];
            return (
              <div
                key={`${row.key}-${d.iso}`}
                className={`avail-grid-cell ${d.iso === todayIso ? 'is-today' : ''}`}
                role="gridcell"
              >
                {slots.map(s => (
                  <SlotCards
                    key={s.id}
                    shift={s}
                    onSpotActions={a => onSpotActions(s, a)}
                    onAssign={() => onAssign(s)}
                  />
                ))}
                {customs.map(c => (
                  <CustomCard
                    key={c.id}
                    declaration={c}
                    onClick={() => onDeclarationActions(c)}
                  />
                ))}
              </div>
            );
          })}
        </React.Fragment>
      ))}

      <div className="avail-grid-rowhead" role="rowheader">On Call</div>
      {days.map(d => {
        const onCall = onCallByDay.get(d.iso) ?? [];
        return (
          <div
            key={`oncall-${d.iso}`}
            className={`avail-grid-cell ${d.iso === todayIso ? 'is-today' : ''}`}
            role="gridcell"
          >
            {onCall.map(c => (
              <div key={c.id} className="avail-card is-oncall" title={c.phone || undefined}>
                <div className="avail-card-name">{c.workingName}</div>
                {c.phone && <div className="avail-card-notes">{c.phone}</div>}
              </div>
            ))}
          </div>
        );
      })}

      <div className="avail-grid-rowhead" role="rowheader">Called Off</div>
      {days.map(d => {
        const off = calledOffByDay.get(d.iso) ?? [];
        return (
          <div
            key={`calledoff-${d.iso}`}
            className={`avail-grid-cell ${d.iso === todayIso ? 'is-today' : ''}`}
            role="gridcell"
          >
            {off.map(c => (
              <CalledOffCard key={c.id} declaration={c} onClick={() => onDeclarationActions(c)} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

// --- Cards ------------------------------------------------------------------

interface SlotCardsProps {
  shift: StaffReservation;
  onSpotActions: (assignment: ReservationAssignment) => void;
  onAssign: () => void;
}

/** Renders one spot as one card. */
function SlotCards({ shift, onSpotActions, onAssign }: SlotCardsProps) {
  const time = `${formatShortTime(shift.startTime)} – ${formatShortTime(shift.endTime)}`;

  if (shift.isCancelled) {
    return (
      <div className="avail-card is-cancelled" aria-label={`${time} cancelled`}>
        <div className="avail-card-time">{time}</div>
        <span className="avail-card-pill pill-cancelled">CANCELLED</span>
      </div>
    );
  }

  // One row is one spot, so one card. This used to draw `capacity` cards from a
  // single row, which is why editing "one reservation" appeared to change several:
  // every card in the group was the same record. Now each card is its own row
  // and editing it moves nothing else.
  const holder = shift.holder;
  return holder
    ? (
      <SpotCard
        time={time}
        label={holder.workingName}
        cls={holder.status === 'PENDING_OVERFLOW' ? 'is-overflow' : 'is-booked'}
        badge={holder.status === 'PENDING_OVERFLOW' ? 'overflow' : undefined}
        notes={shift.notes}
        title="Call off or edit this spot"
        onClick={() => onSpotActions(holder)}
      />
    )
    : (
      <SpotCard
        time={time}
        label="OPEN"
        cls="is-open"
        notes={shift.notes}
        title="Assign a provider, or edit this spot"
        onClick={onAssign}
      />
    );
}

interface SpotCardProps {
  time: string;
  label: string;
  cls: string;
  badge?: string;
  notes?: string | null;
  title: string;
  onClick: () => void;
}

function SpotCard({ time, label, cls, badge, notes, title, onClick }: SpotCardProps) {
  return (
    <button
      type="button"
      className={`avail-card ${cls}`}
      onClick={onClick}
      title={title}
    >
      <div className="avail-card-time">{time}</div>
      <span className={`avail-card-pill ${cls}`}>{label}</span>
      {badge && <span className="avail-card-badge">{badge}</span>}
      {notes && <div className="avail-card-notes">{notes}</div>}
    </button>
  );
}

/**
 * Custom-hours declaration. Yellow so it reads as off-roster: this provider
 * proposed her own times and a manager approved them, rather than taking a
 * published spot.
 *
 * Clicking opens the declaration's details and, from there, a call-off with
 * the same event trail as a rostered spot. The hours themselves aren't
 * editable here — she declared them.
 */
function CustomCard({
  declaration, onClick,
}: { declaration: WeekDeclaration; onClick: () => void }) {
  const time = declaration.customStart && declaration.customEnd
    ? `${formatShortTime(declaration.customStart)} – ${formatShortTime(declaration.customEnd)}`
    : 'Custom hours';

  return (
    <button
      type="button"
      className="avail-card is-custom"
      onClick={onClick}
      title="Custom hours — call off or view details"
    >
      <div className="avail-card-time">{time}</div>
      <div className="avail-card-name">{declaration.workingName}</div>
      <span className="avail-card-badge">custom</span>
    </button>
  );
}

/**
 * A provider who rang ahead and pulled out. Her spot has already reopened as
 * OPEN in the roster rows above — this card is the record of why it's empty,
 * and the way back if the call-off was logged in error.
 */
function CalledOffCard({
  declaration, onClick,
}: { declaration: WeekDeclaration; onClick: () => void }) {
  const time = declaration.shiftStart && declaration.shiftEnd
    ? `${formatShortTime(declaration.shiftStart)} – ${formatShortTime(declaration.shiftEnd)}`
    : declaration.customStart && declaration.customEnd
      ? `${formatShortTime(declaration.customStart)} – ${formatShortTime(declaration.customEnd)}`
      : null;

  const reason = declaration.cancelReason
    ? declaration.cancelReason.toLowerCase().replace(/_/g, ' ')
    : null;

  return (
    <button
      type="button"
      className="avail-card is-calledoff"
      onClick={onClick}
      title="Reverse call-off"
    >
      {time && <div className="avail-card-time">{time}</div>}
      <div className="avail-card-name">{declaration.workingName}</div>
      {(reason || declaration.cancelNotes) && (
        <div className="avail-card-notes">
          {reason}{reason && declaration.cancelNotes ? ' — ' : ''}{declaration.cancelNotes}
        </div>
      )}
    </button>
  );
}

// --- Helpers ----------------------------------------------------------------

/**
 * Fallback row for custom hours approved before the manager could choose one.
 * Same shape as the one-time backfill in the add_shift_section migration:
 * starts after midnight is graveyard, finishes by 10pm the same day is day,
 * anything else (including overnight) is night.
 */
export function deriveSection(start: string | null, end: string | null): DayPart {
  if (!start || !end) return 'NIGHT';
  if (start < '05:00') return 'GRAVEYARD';
  if (end > start && end <= '22:00') return 'DAY';
  return 'NIGHT';
}

interface DayCol {
  iso: string;
  weekday: string;
  dayNum: number;
}

function buildDays(weekStart: Date): DayCol[] {
  const out: DayCol[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    out.push({
      iso: isoDate(d),
      weekday: d.toLocaleDateString('en-AU', { weekday: 'short' }),
      dayNum: d.getDate(),
    });
  }
  return out;
}

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** "18:00" -> "6pm", "18:30" -> "6:30pm", "00:00" -> "12am". */
export function formatShortTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}
